package de.oevbs.kunde.kundenportal2.services.mydata

import de.oevbs.businesspartner.bpcrmservice2.partner.model.Address
import de.oevbs.kunde.kundenportal2.ContactInformationStep
import de.oevbs.kunde.kundenportal2.Formats
import de.oevbs.kunde.kundenportal2.authentication.PortalSession
import de.oevbs.kunde.kundenportal2.authentication.PortalSessionMiddleware
import de.oevbs.kunde.kundenportal2.components.GlobalServiceMessagesStore
import de.oevbs.kunde.kundenportal2.components.globalInitTracker
import de.oevbs.kunde.kundenportal2.components.globalSubmitTracker
import de.oevbs.kunde.kundenportal2.model.services.mydata.ContactInformation
import de.oevbs.kunde.kundenportal2.model.services.mydata.mail
import de.oevbs.kunde.kundenportal2.util.tryCatchAndHandleErrors
import de.oevbs.sfa.communication.use
import dev.fritz2.core.storeOf
import dev.fritz2.headless.validation.ComponentValidationMessage
import dev.fritz2.validation.ValidatingStore
import dev.fritz2.validation.valid
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

class ContactInformationStore(job: Job) : ValidatingStore<ContactInformation, Boolean, ComponentValidationMessage>(
    ContactInformation(),
    ContactInformation.validation,
    metadataDefault = false,
    job,
    validateAfterUpdate = false
) {
    private val client = MyDataClient().use(PortalSessionMiddleware)

    private val initFailedStore = storeOf(false, Job())
    val initFailedFlow: Flow<Boolean> = initFailedStore.data

    private val progressStore = storeOf(ContactInformationStep.CONTACTINFO1, job)
    val currentStep: Flow<ContactInformationStep> = progressStore.data


    private var initial: ContactInformation = ContactInformation()

    val hasInitialData: Flow<Boolean> = data.map { it == initial }

    val mailWasEdited: Flow<Boolean> = map(ContactInformation.mail() + Formats.trimString).data.map {
        it != initial.mail
    }

    val validateMailRepeat = handle<Boolean> { model, mailWasEdited ->
        validate(model, metadata = mailWasEdited)
        model
    }

    val submitAddressChoice = handle<Address> { model, addressChoice ->
        GlobalServiceMessagesStore.clear(Unit)

        if (!validate(model).valid) {
            return@handle model
        }

        globalSubmitTracker.track {
            val savedContactInfo =
                changePartnerDataRequest(model.copy(forceUseUserInputAddress = true, address = addressChoice))
            if (savedContactInfo != null) {
                progressStore.update(ContactInformationStep.SUCCESS3)
                initial = savedContactInfo
                savedContactInfo
            } else model
        }
    }

    val submitChanges = handle<Boolean> { model, mailWasEdited ->
        GlobalServiceMessagesStore.clear(Unit)

        if (!validate(model, metadata = mailWasEdited).valid) {
            return@handle model
        }

        globalSubmitTracker.track {
            val savedContactInfo = changePartnerDataRequest(model.copy(forceUseUserInputAddress = false))
            if (savedContactInfo != null) {
                if (progressStore.current == ContactInformationStep.CONTACTINFO1 &&
                    savedContactInfo.addressSuggestion != Address()
                ) {
                    progressStore.update(ContactInformationStep.ADDRESS2)
                } else {
                    progressStore.update(ContactInformationStep.SUCCESS3)
                    //Wenn der Kunde seinen Nachnamen angepasst hat, das Session-Popup mit dem neuen Namen
                    // aktualisieren. Dies geschieht über einen Aufruf des oeauth-server Endpunkts info.
                    if (initial.lastName != savedContactInfo.lastName) {
                        PortalSession.refreshJwt()
                    }
                    initial = savedContactInfo
                }
                savedContactInfo
            } else model
        }
    }

    val initPartnerData = handle { model ->
        GlobalServiceMessagesStore.clear(Unit)

        globalInitTracker.track {
            resetMessages()
            val initData = initPartnerDataRequest()
            if (initData == null) {
                initFailedStore.update(true)
                return@track model
            }
            initial = initData
            initData
        }
    }

    private suspend fun changePartnerDataRequest(contactInformation: ContactInformation): ContactInformation? {
        return tryCatchAndHandleErrors("Speichern der Partner-Kontaktinformationen") {
            client.saveContactInfo(contactInformation)
        }
    }

    private suspend fun initPartnerDataRequest(): ContactInformation? {
        return tryCatchAndHandleErrors("Initialisieren der Partner-Kontaktinformationen") {
            client.initContactInfo()
        }
    }
}